// The only place the app opens the microphone (`getUserMedia`; lint-enforced).
//
// iOS (issue #254): after the page has been backgrounded (app switch, screen
// lock), iOS's voice-processing input path can go dead. getUserMedia still
// returns a live, unmuted track, but every sample is exactly 0, and speech
// recognition hears nothing. A stream with echo cancellation, noise
// suppression and auto gain off uses a separate input path. Holding a live
// stream open for the whole turn keeps the recognizer hearing the learner.
// Either path can be dead on a turn after returning (not only the first),
// so the other one is the fallback. Device spike: frontend/dev/ios-background-mic-spike.html.
//
// So on iOS every turn opens the raw stream, checks it delivers real samples,
// retries once with default constraints if it's silent, and gives up with
// MicUnavailableError (the UI offers a reload) if that's silent too.
// Desktop opens a plain stream with no check.

import { diag } from './diag';

/** How long a stream may stay digitally silent before it's considered dead. */
export const LIVENESS_WINDOW_MS = 600;
const POLL_MS = 20;

/** Voice processing off: the input path that survives backgrounding on iOS. */
export const RAW_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

/** The microphone delivers only silence and nothing brought it back. */
export class MicUnavailableError extends Error {
  constructor() {
    super('The microphone stopped working. Reload the page to fix it.');
    this.name = 'MicUnavailableError';
  }
}

/** The turn was cancelled while the mic was being opened. */
export class MicCancelledError extends Error {
  constructor() {
    super('Microphone acquisition cancelled');
    this.name = 'MicCancelledError';
  }
}

/** An open microphone. Call `release()` exactly when the turn is done. */
export interface MicHandle {
  stream: MediaStream;
  release(): void;
}

/** Injectable browser APIs (defaults use the real ones). */
export interface MicDeps {
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createAudioContext?: () => AudioContext;
}

interface AcquireOptions {
  /** iOS: raw constraints, liveness check, one default-constraints retry. */
  ios: boolean;
  /** Checked between async steps; true stops and throws MicCancelledError. */
  isCancelled?: () => boolean;
}

function defaultGetUserMedia():
  | ((c: MediaStreamConstraints) => Promise<MediaStream>)
  | undefined {
  const devices =
    typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  return devices?.getUserMedia ? devices.getUserMedia.bind(devices) : undefined;
}

/** Whether this browser can open a microphone at all. */
export function canOpenMic(): boolean {
  return defaultGetUserMedia() !== undefined;
}

function defaultAudioContext(): AudioContext {
  return new window.AudioContext();
}

function stopStream(stream: MediaStream): void {
  stream.getTracks().forEach((track) => track.stop());
}

/** A stream plus the meter that watched it (kept open with the stream). */
interface Probe {
  stream: MediaStream;
  context: AudioContext;
  alive: boolean;
}

function closeProbe(probe: Probe): void {
  stopStream(probe.stream);
  void probe.context.close().catch(() => undefined);
}

/** Wait until the stream delivers a non-zero sample, or the window runs out. */
function waitForAudio(
  context: AudioContext,
  stream: MediaStream,
  isCancelled: () => boolean
): Promise<boolean> {
  const analyser = context.createAnalyser();
  analyser.fftSize = 2048;
  context.createMediaStreamSource(stream).connect(analyser);
  if (context.state === 'suspended') void context.resume();
  const data = new Float32Array(2048);
  const startedAt = Date.now();
  return new Promise((resolve) => {
    const poll = (): void => {
      analyser.getFloatTimeDomainData(data);
      if (data.some((sample) => sample !== 0)) {
        resolve(true);
      } else if (
        isCancelled() ||
        Date.now() - startedAt >= LIVENESS_WINDOW_MS
      ) {
        resolve(false);
      } else {
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
  });
}

/**
 * Open the microphone for one turn. Call after the audio-session capture
 * claim. Rejects with the browser's own error (e.g. `NotAllowedError`), with
 * {@link MicUnavailableError} for a dead mic, or with {@link MicCancelledError}.
 */
export async function acquireMic(
  options: AcquireOptions,
  deps: MicDeps = {}
): Promise<MicHandle> {
  const getUserMedia =
    'getUserMedia' in deps ? deps.getUserMedia : defaultGetUserMedia();
  const createAudioContext = deps.createAudioContext ?? defaultAudioContext;
  const isCancelled = options.isCancelled ?? ((): boolean => false);
  if (!getUserMedia) {
    throw new Error('getUserMedia is not available in this browser');
  }

  if (!options.ios) {
    const stream = await getUserMedia({ audio: true });
    if (isCancelled()) {
      stopStream(stream);
      throw new MicCancelledError();
    }
    return { stream, release: () => stopStream(stream) };
  }

  const attempts: Array<{
    label: string;
    audio: MediaTrackConstraints | true;
  }> = [
    { label: 'raw', audio: RAW_CONSTRAINTS },
    { label: 'default', audio: true },
  ];
  for (const attempt of attempts) {
    const openedAt = Date.now();
    const stream = await getUserMedia({ audio: attempt.audio });
    let context: AudioContext;
    try {
      context = createAudioContext();
    } catch (err) {
      stopStream(stream);
      throw err;
    }
    const probe: Probe = { stream, context, alive: false };
    if (isCancelled()) {
      closeProbe(probe);
      throw new MicCancelledError();
    }
    try {
      probe.alive = await waitForAudio(context, stream, isCancelled);
    } catch (err) {
      closeProbe(probe);
      throw err;
    }
    diag(
      'mic:',
      attempt.label,
      probe.alive ? 'live' : 'SILENT',
      'after',
      Date.now() - openedAt,
      'ms'
    );
    if (isCancelled()) {
      closeProbe(probe);
      throw new MicCancelledError();
    }
    if (probe.alive) {
      return { stream, release: () => closeProbe(probe) };
    }
    closeProbe(probe);
  }
  throw new MicUnavailableError();
}
